import React, { useMemo } from "react";
import { User as UserIcon, X } from "lucide-react";
import { Role } from "types";
import PureDropDown from "components/pure-dropdown";
import { useTranslation } from "react-i18next";

interface UserToAdd {
  email: string;
  roleId?: string;
}

interface UsersAddingFormTableProps {
  selectedUsers: UserToAdd[];
  availableRoles: Role[];
  rolesLoading: boolean;
  onRemoveUser: (email: string) => void;
  onUpdateUserRole: (email: string, roleId: string) => void;
}


const UsersAddingFormTable: React.FC<UsersAddingFormTableProps> = ({
  selectedUsers,
  availableRoles,
  rolesLoading,
  onRemoveUser,
  onUpdateUserRole,
}) => {
  const { t } = useTranslation();
  const roleOptions = useMemo(() => 
    availableRoles.map(role => ({
      value: role.id,
      label: role.name
    })), [availableRoles]
  );

  return (
    <div className="mt-6 rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {t("workspaces.usersDialog.selectedUsers")}
          </h3>
          <span className="rounded-full bg-brand-100 px-2.5 py-0.5 text-xs font-medium text-brand-800 dark:bg-brand-900 dark:text-brand-200">
            {t("workspaces.usersDialog.selectedCount", { count: selectedUsers.length })}
          </span>
        </div>
      </div>
      {selectedUsers.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <div className="flex flex-col items-center justify-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
              <UserIcon className="h-6 w-6 text-gray-400" />
            </div>
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              {t("workspaces.usersDialog.noSelected")}
            </p>
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              {t("workspaces.usersDialog.selectUsersHint")}
            </p>
          </div>
        </div>
      ) : (
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-700">
            <th className="px-3 py-3 text-left text-xs font-semibold tracking-wider text-gray-500 dark:text-gray-400" style={{width: '60%'}}>
              {t("common.email")}
            </th>
            <th className="px-3 py-3 text-left text-xs font-semibold tracking-wider text-gray-500 dark:text-gray-400" style={{width: '30%'}}>
              {t("workspaces.usersDialog.role")}
            </th>
            <th className="px-3 py-3 text-center text-xs font-semibold tracking-wider text-gray-500 dark:text-gray-400" style={{width: '10%'}}>
              
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {selectedUsers.map((user) => {
            const hasRole = user.roleId;
            return (
             <tr key={user.email} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${!hasRole ? 'bg-red-50/50 dark:bg-red-900/5' : ''}`}>
              <td className="px-3 py-3 align-middle" style={{width: '60%'}}>
                <div className="flex items-start gap-2">
                  <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-600 mt-0.5">
                    <UserIcon className="h-3 w-3 text-gray-600 dark:text-gray-300" />
                  </div>
                   <div className="min-w-0 flex-1">
                     <p className="break-all text-xs font-medium text-gray-900 dark:text-white leading-tight">
                       {user.email}
                     </p>
                   </div>
                </div>
              </td>
              <td className="px-3 py-3 align-middle" style={{width: '30%'}}>
                <div className="space-y-1">
                  <PureDropDown
                    options={roleOptions}
                    value={user.roleId || undefined}
                    onChange={(value) => {
                      const roleValue = Array.isArray(value) ? value[0] : value;
                      if (roleValue && roleOptions.some(option => option.value === roleValue)) {
                        onUpdateUserRole(user.email, roleValue);
                      }
                    }}
                    placeholder={t("workspaces.usersDialog.selectRole")}
                    disabled={rolesLoading}
                    showClearButton={false}
                    className={!hasRole ? "border-red-300 focus:border-red-500" : ""}
                  />
                  {!hasRole && (
                    <div className="text-xs text-red-500">
                      {t("workspaces.usersDialog.roleRequired")}
                    </div>
                  )}
                </div>
              </td>
              <td className="px-3 py-3 text-center align-middle" style={{width: '10%'}}>
                <button
                  type="button"
                  onClick={() => onRemoveUser(user.email)}
                  className="inline-flex h-6 w-6 items-center justify-center rounded-md text-red-500 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400 disabled:text-gray-400 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                  title={t("workspaces.usersDialog.removeUser")}
                >
                  <X size={14} />
                </button>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
      )}
    </div>
  );
};

export default UsersAddingFormTable;
